import * as React from "react";
import { Dialog, FormGroup, InputGroup, Button } from "@blueprintjs/core";
import { css } from "@emotion/core";

interface FolderFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSubmit: (name: string) => void | Promise<void>;
  initialValue?: string;
  isLoading: boolean;
  loadingText?: string;
  submitText?: string;
}

export function FolderFormDialog(props: FolderFormDialogProps) {
  const {
    isOpen,
    onClose,
    title,
    onSubmit,
    initialValue = "",
    isLoading,
    loadingText = "Saving...",
    submitText = "Save",
  } = props;

  const [errors, setErrors] = React.useState<string[]>([]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formErrors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "folder-name"
    ) as HTMLInputElement;

    const name = nameInput?.value;

    if (!name || name.trim() === "") {
      formErrors.push("name");
    }

    if (formErrors.length === 0) {
      await onSubmit(name.trim());
      setErrors([]);
    } else {
      setErrors(formErrors);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors([]);
      onClose();
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title={title}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="folder-name"
            labelInfo="(required)"
            intent={errors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="folder-name"
              intent={errors.includes("name") ? "danger" : "none"}
              disabled={isLoading}
              defaultValue={initialValue}
              autoFocus
            />
          </FormGroup>
        </div>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {errors.length > 0 &&
              `Missing or invalid fields: ${errors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={isLoading}
              intent="none"
              onClick={handleClose}
            />
            <Button
              text={isLoading ? loadingText : submitText}
              disabled={isLoading}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}
